/**
 * DeliverPiecesModal — confirm customer handoff for pending deliverable pieces.
 * Same ConfirmationModal pattern as CloseConfirmationModal / ConfirmSaleConfirmationModal.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography, Stack, Divider } from '@mui/material';
import { LocalShipping as LocalShippingIcon } from '@mui/icons-material';
import { isValid } from 'date-fns';
import { ConfirmationModal } from '../../../ui/Modal';
import { CloseConfirmationModal } from '../../../modals/confirmation';
import { CommercialDateModeSection } from '../../shared/CommercialDateTimePicker';
import { useOrdersPermissions } from '../../../../hooks/useOrdersPermissions';
import MissingRequirementsHint from '../../shared/MissingRequirementsHint';
import type { CommercialDateMode } from '../../../../utils/sales/commercial-sale-date';
import {
  commercialDateOverrideIso,
  isCommercialDateChoiceComplete,
} from '../../../../utils/sales/commercial-sale-date';
import type { SalesOrder, SaleFulfillmentDetail } from '../../../../types/sales.types';
import {
  getDeliverableUndeliveredItems,
  getItemDisplayName,
} from '../../../../utils/sales/order-delivery.utils';

export interface DeliverPiecesConfirmPayload {
  items: Array<{ itemId: number; isDelivered: boolean }>;
  /** ISO override when user chose a custom delivery date; omit for server now. */
  deliveredAt?: string;
}

export interface DeliverPiecesModalProps {
  open: boolean;
  order: SalesOrder | null;
  fulfillmentDetail?: SaleFulfillmentDetail | null;
  onClose: () => void;
  onConfirm: (payload: DeliverPiecesConfirmPayload) => void;
  loading?: boolean;
}

const DeliverPiecesModal: React.FC<DeliverPiecesModalProps> = ({
  open,
  order,
  fulfillmentDetail = null,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const { canModifySaleDate } = useOrdersPermissions();
  const deliverable = useMemo(
    () => (order ? getDeliverableUndeliveredItems(order, fulfillmentDetail) : []),
    [order, fulfillmentDetail],
  );

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [deliveryDateMode, setDeliveryDateMode] = useState<CommercialDateMode | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<Date>(() => new Date());
  const [dateModeError, setDateModeError] = useState(false);
  const [dateValueError, setDateValueError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setShowCloseConfirmation(false);
      setDateModeError(false);
      setDateValueError(null);
      return;
    }
    // Default: all deliverable pieces ON (typical handoff).
    setSelectedIds(new Set(deliverable.map((item) => item.id)));
    setDeliveryDateMode(null);
    setDeliveryDate(new Date());
    setDateModeError(false);
    setDateValueError(null);
  }, [open, deliverable]);

  const defaultAllSelected = useMemo(() => {
    if (deliverable.length === 0) return true;
    if (selectedIds.size !== deliverable.length) return false;
    return deliverable.every((item) => selectedIds.has(item.id));
  }, [deliverable, selectedIds]);

  const toggle = (itemId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  const missingDeliveryDate =
    canModifySaleDate && !isCommercialDateChoiceComplete(deliveryDateMode, deliveryDate);

  const handleDateModeChange = (mode: CommercialDateMode) => {
    setDeliveryDateMode(mode);
    setDeliveryDate(new Date());
    setDateModeError(false);
    setDateValueError(null);
  };

  const handleConfirm = () => {
    if (selectedIds.size === 0) return;
    if (canModifySaleDate && deliveryDateMode == null) {
      setDateModeError(true);
      return;
    }
    if (canModifySaleDate && deliveryDateMode === 'custom' && !isValid(deliveryDate)) {
      setDateValueError('Seleccione una fecha y hora de entrega válida.');
      return;
    }
    setDateModeError(false);
    setDateValueError(null);
    const deliveredAtOverride =
      canModifySaleDate && deliveryDateMode != null
        ? commercialDateOverrideIso(deliveryDateMode, deliveryDate)
        : undefined;
    onConfirm({
      items: Array.from(selectedIds).map((itemId) => ({
        itemId,
        isDelivered: true,
      })),
      ...(deliveredAtOverride != null ? { deliveredAt: deliveredAtOverride } : {}),
    });
  };

  const handleCloseClick = useCallback(() => {
    if (loading) return;
    if (!defaultAllSelected) {
      setShowCloseConfirmation(true);
      return;
    }
    onClose();
  }, [defaultAllSelected, loading, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirmation(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirmation(false);
  }, []);

  return (
    <>
      <ConfirmationModal
        open={open}
        onClose={handleCloseClick}
        onConfirm={handleConfirm}
        title="¿Entregar piezas?"
        message="Se marcarán como entregadas las piezas seleccionadas que están disponibles en sucursal."
        confirmText="Confirmar"
        cancelText="Volver"
        variant="default"
        loading={loading}
        disabled={selectedIds.size === 0 || missingDeliveryDate}
        icon={<LocalShippingIcon />}
        customContent={
          deliverable.length > 0 ? (
            <Box sx={{ width: '100%', mt: 1, textAlign: 'left' }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selecciona las piezas a entregar ({selectedIds.size} de {deliverable.length})
              </Typography>
              <Stack spacing={0.25}>
                {deliverable.map((item) => (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(item.id)}
                        onChange={(_, checked) => toggle(item.id, checked)}
                        disabled={loading}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{getItemDisplayName(item)}</Typography>
                        {item.warehouseInventoryProduct?.productCodeUnique && (
                          <Typography variant="caption" color="text.secondary">
                            {item.warehouseInventoryProduct.productCodeUnique}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Stack>

              {canModifySaleDate && (
                <Box sx={{ mt: 2 }}>
                  <CommercialDateModeSection
                    title="Fecha de entrega"
                    mode={deliveryDateMode}
                    onModeChange={handleDateModeChange}
                    value={deliveryDate}
                    onChange={(date) => {
                      setDeliveryDate(date);
                      setDateValueError(null);
                    }}
                    pickerLabel="Fecha y hora de la entrega"
                    currentHelperText="Se usará la fecha y hora en que se confirme la entrega."
                    groupAriaLabel="Fecha comercial de la entrega"
                    disabled={loading}
                    error={dateModeError}
                  />
                  {dateValueError != null ? (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                      {dateValueError}
                    </Typography>
                  ) : null}
                </Box>
              )}
              <MissingRequirementsHint missingDate={missingDeliveryDate} sx={{ mt: 1.5 }} />
            </Box>
          ) : null
        }
      />

      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default DeliverPiecesModal;
